import { createBrowserRouter } from "react-router-dom";

import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/feedback/RouteErrorBoundary";
import { AppShell } from "@/components/layout/AppShell";
import { AgentEditorPage } from "@/features/agents/pages/AgentEditorPage";
import { AgentListPage } from "@/features/agents/pages/AgentListPage";
import { AuditLogPage } from "@/features/audit-log/pages/AuditLogPage";
import { CallbackPage } from "@/features/auth/pages/CallbackPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { MePage } from "@/features/auth/pages/MePage";
import { DirectChatPage } from "@/features/chat/pages/DirectChatPage";
import { CompanyContextPage } from "@/features/company-context/pages/CompanyContextPage";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { HitlInboxPage } from "@/features/hitl/pages/HitlInboxPage";
import { HomePage } from "@/features/home/pages/HomePage";
import { KbDetailPage } from "@/features/knowledge-bases/pages/KbDetailPage";
import { KbListPage } from "@/features/knowledge-bases/pages/KbListPage";
import { LlmCatalogPage } from "@/features/llms/pages/LlmCatalogPage";
import { AgentPublicDetailPage } from "@/features/marketplace/pages/AgentPublicDetailPage";
import { MarketplacePage } from "@/features/marketplace/pages/MarketplacePage";
import { McpListPage } from "@/features/mcps/pages/McpListPage";
import { MeUsagePage } from "@/features/observability/pages/MeUsagePage";
import { ObservabilityHomePage } from "@/features/observability/pages/ObservabilityHomePage";
import { PiiConfigPage } from "@/features/pii/pages/PiiConfigPage";
import { PromptTonesPage } from "@/features/prompt-tones/pages/PromptTonesPage";
import { ScheduleEditorPage } from "@/features/scheduler/pages/ScheduleEditorPage";
import { SchedulerListPage } from "@/features/scheduler/pages/SchedulerListPage";
import { ServiceUserDetailPage } from "@/features/service-users/pages/ServiceUserDetailPage";
import { ServiceUsersListPage } from "@/features/service-users/pages/ServiceUsersListPage";
import { SkillListPage } from "@/features/skills/pages/SkillListPage";
import { TaskDetailPage } from "@/features/tasks/pages/TaskDetailPage";
import { TasksListPage } from "@/features/tasks/pages/TasksListPage";
import { TeamSessionChatPage } from "@/features/team-sessions/pages/TeamSessionChatPage";
import { TeamSessionEditorPage } from "@/features/team-sessions/pages/TeamSessionEditorPage";
import { TeamSessionsListPage } from "@/features/team-sessions/pages/TeamSessionsListPage";
import { TeamsListPage } from "@/features/teams/pages/TeamsListPage";
import { ChatPage } from "@/features/threads/pages/ChatPage";
import { ThreadsListPage } from "@/features/threads/pages/ThreadsListPage";
import { UsersListPage } from "@/features/users/pages/UsersListPage";

export const router = createBrowserRouter(
  [
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/auth/callback",
    element: <CallbackPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: "/",
            element: <HomePage />,
            handle: { crumbKey: "nav.crumbs.home", fullbleed: true },
          },
          {
            path: "/me",
            element: <MePage />,
            handle: { crumbKey: "nav.crumbs.me" },
          },
          {
            path: "/teams",
            element: <TeamsListPage />,
            handle: { crumbKey: "nav.crumbs.teams" },
          },
          {
            path: "/users",
            element: <UsersListPage />,
            handle: { crumbKey: "nav.crumbs.users" },
          },
          {
            path: "/admin/service-users",
            element: <ServiceUsersListPage />,
            handle: { crumbKey: "nav.crumbs.serviceUsers" },
          },
          {
            path: "/admin/service-users/:serviceUserId",
            element: <ServiceUserDetailPage />,
            handle: { crumbKey: "nav.crumbs.serviceUserDetail" },
          },
          {
            path: "/company-context",
            element: <CompanyContextPage />,
            handle: { crumbKey: "nav.crumbs.companyContext" },
          },
          {
            path: "/knowledge-bases",
            element: <KbListPage />,
            handle: { crumbKey: "nav.crumbs.knowledgeBases" },
          },
          {
            path: "/knowledge-bases/:kbId",
            element: <KbDetailPage />,
            handle: { crumbKey: "nav.crumbs.kbDetail" },
          },
          {
            path: "/llm-models",
            element: <LlmCatalogPage />,
            handle: { crumbKey: "nav.crumbs.llmModels" },
          },
          {
            path: "/mcps",
            element: <McpListPage />,
            handle: { crumbKey: "nav.crumbs.mcps" },
          },
          {
            path: "/skills",
            element: <SkillListPage />,
            handle: { crumbKey: "nav.crumbs.skills" },
          },
          {
            path: "/me/agents",
            element: <AgentListPage />,
            handle: { crumbKey: "nav.crumbs.myAgents" },
          },
          {
            path: "/me/agents/new",
            element: <AgentEditorPage />,
            handle: { crumbKey: "nav.crumbs.newAgent" },
          },
          {
            path: "/me/agents/:agentId",
            element: <AgentEditorPage />,
            handle: { crumbKey: "nav.crumbs.editAgent" },
          },
          {
            path: "/admin/prompt-tones",
            element: <PromptTonesPage />,
            handle: { crumbKey: "nav.crumbs.promptTones" },
          },
          {
            path: "/threads",
            element: <ThreadsListPage />,
            handle: { crumbKey: "nav.crumbs.threads" },
          },
          {
            // M8: chat direto com LLM. Layout proprio com sidebar
            // interno (historico). /chat sem id = "tela vazia" + composer.
            // ``flush``: ocupa 100% do espaco (sem padding do AppShell)
            // pra sidebar interna encostar no sidebar global.
            path: "/chat",
            element: <DirectChatPage />,
            handle: {
              crumbKey: "nav.crumbs.directChat",
              flush: true,
            },
          },
          {
            path: "/chat/:threadId",
            element: <DirectChatPage />,
            handle: {
              crumbKey: "nav.crumbs.directChat",
              flush: true,
            },
          },
          {
            path: "/threads/new",
            element: <ChatPage />,
            handle: {
              crumbKey: "nav.crumbs.newConversation",
              fullbleed: true,
            },
          },
          {
            path: "/threads/:threadId",
            element: <ChatPage />,
            handle: {
              crumbKey: "nav.crumbs.conversation",
              fullbleed: true,
            },
          },
          {
            path: "/me/pending-approvals",
            element: <HitlInboxPage />,
            handle: { crumbKey: "nav.crumbs.approvals" },
          },
          {
            path: "/marketplace",
            element: <MarketplacePage />,
            handle: { crumbKey: "nav.crumbs.marketplace" },
          },
          {
            path: "/marketplace/:agentId",
            element: <AgentPublicDetailPage />,
            handle: { crumbKey: "nav.crumbs.agentDetail" },
          },
          {
            path: "/sessions",
            element: <TeamSessionsListPage />,
            handle: { crumbKey: "nav.crumbs.teamSessions" },
          },
          {
            path: "/sessions/new",
            element: <TeamSessionEditorPage />,
            handle: { crumbKey: "nav.crumbs.newSession" },
          },
          {
            path: "/sessions/:sessionId",
            element: <TeamSessionEditorPage />,
            handle: { crumbKey: "nav.crumbs.editSession" },
          },
          {
            path: "/sessions/:sessionId/chat",
            element: <TeamSessionChatPage />,
            handle: { crumbKey: "nav.crumbs.chat", fullbleed: true },
          },
          {
            path: "/tasks",
            element: <TasksListPage />,
            handle: { crumbKey: "nav.crumbs.tasks" },
          },
          {
            path: "/tasks/:taskId",
            element: <TaskDetailPage />,
            handle: { crumbKey: "nav.crumbs.taskDetail" },
          },
          {
            path: "/scheduler",
            element: <SchedulerListPage />,
            handle: { crumbKey: "nav.crumbs.scheduler" },
          },
          {
            path: "/scheduler/new",
            element: <ScheduleEditorPage />,
            handle: { crumbKey: "nav.crumbs.newSchedule" },
          },
          {
            path: "/scheduler/:scheduleId",
            element: <ScheduleEditorPage />,
            handle: { crumbKey: "nav.crumbs.editSchedule" },
          },
          {
            path: "/admin/audit-log",
            element: <AuditLogPage />,
            handle: { crumbKey: "nav.crumbs.auditLog" },
          },
          {
            path: "/pii",
            element: <PiiConfigPage />,
            handle: { crumbKey: "nav.crumbs.piiFilter" },
          },
          {
            path: "/observability",
            element: <ObservabilityHomePage />,
            handle: { crumbKey: "nav.crumbs.observability" },
          },
          {
            path: "/observability/me",
            element: <MeUsagePage />,
            handle: { crumbKey: "nav.crumbs.myUsage" },
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
  ],
  {
    // Opta pelos comportamentos do React Router v7 antes do major bump --
    // remove os warnings de "Future Flag" no console.
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
